import { Box } from '@mui/material';

import EmptyView from 'Lib/components/common/table/EmptyView';
import { getFileIcon, isPDF } from 'Lib/utilities/files';

import ZoomableImage from '../zoomableMedia/ZoomableImage';
import ZoomablePDF from '../zoomableMedia/ZoomablePDF';

import IconThumbnail from './thumbnails/IconThumbnail';

// TODO: add global jsdoc type for file response object & enums

/**
 * @typedef {function} getSrc
 * @param {FileParent} fileParent
 * @param {PortalType} portalType
 * @returns
 */
export const getSrc = (fileParent, portalType, objectID, objectTypeID, portalID, leaseID, portfolioID) => {
    const isManagerPortal = portalType === 'manager';
    const isResidentPortal = portalType === 'tenant';
    const isOwnerPortal = portalType === 'owner';
    if (isResidentPortal) portalType = 'resident';

    const { fileID, previewFileName, fileType } = fileParent.file;
    const base = (type = 'preview') => `/api/${isResidentPortal ? 'resident/lease' : portalType}/files/${fileID}/${type}`;
    const params = `?${isResidentPortal ? `leaseID=${leaseID}&` : ''}${
        isOwnerPortal ? `portfolioID=${portfolioID}&` : ''
    }objectTypeID=${objectTypeID}&objectID=${objectID}&portalID=${portalID}`;

    let fileSrc = `${base()}${isManagerPortal ? '' : params}`;
    let previewSrc = '';

    if (previewFileName !== null) {
        // if file preview exists, use it as the previewSrc
        previewSrc = `${base('thumbnail')}${isManagerPortal ? '' : params}`;
    } else if (fileType === 'pdf') {
        // covers pdfs without previews which is a fringe server issue or a password protected PDF
        previewSrc = `picture_as_pdf`;
    } else {
        const icon = getFileIcon(fileParent);
        fileSrc = previewSrc = icon;
    }

    return { fileSrc, previewSrc };
};

/** @typedef {object} FileItem
 * @property {string} title - The title of the file.
 * @property {string} src - The source of the file.
 * @property {string} previewSrc - The source of the preview.
 * @property {string} type - The type of the file.
 */

/**
 * @typedef {function} createItems
 * @param {FileParent[]} files - The array of files to be displayed in the carousel.
 * @param {string} portalType - The portal type.
 * @returns {FileItem[]} The array of items to be displayed in the carousel.
 */
// loop through files and create an array of items
export const createItems = (files, portalType, objectID, objectTypeID, portalID, leaseID, portfolioID) =>
    files.files.map(file => {
        const { fileSrc, previewSrc } = getSrc(file, portalType, objectID, objectTypeID, portalID, leaseID, portfolioID);

        return {
            title: file.file,
            src: fileSrc,
            previewSrc: previewSrc,
            type: isPDF(file) ? 'pdf' : file.file.previewFileName ? 'image' : 'icon'
        };
    });

/**
 * @typedef {function} createFileSlides
 * @param {FileItem[]} items
 * @returns {JSX.Element[]}
 */
// Creates a slide for each item.
export const createFileSlides = items => {
    return items.map((item, index) => {
        if (item.type === 'image') {
            return <ZoomableImage key={index} src={`${item.src}`} />;
        } else if (item.type === 'pdf') {
            return <ZoomablePDF key={index} src={`${item.src}`} />;
        } else {
            return <EmptyView key={index} grow iconName={item.src || 'article'} title="Preview Unavailable" />; /* if the media type is not handled, show an empty view */
        }
    });
};

/**
 * @typedef {function} createThumbnails
 * @param {FileItem[]} items
 * @returns {JSX.Element[]}
 */
export const createThumbnails = items => {
    return items.map((item, index) => {
        if (typeof item.previewSrc === 'string' && item.previewSrc.includes('/')) {
            return (
                <Box
                    key={index}
                    component="img"
                    src={`${item.previewSrc}`}
                    sx={{
                        zIndex: '1',
                        width: 1,
                        height: 1,
                        objectFit: 'cover'
                    }}
                />
            );
        }

        return <IconThumbnail key={index} iconName={item.previewSrc} index={index} />;
    });
};
